import React, {
  createContext,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  FooterActionBar,
  Loader,
  ModalIsland,
} from '@saviynt/design-system';
import { type Action, type Dispatch } from 'redux';

import { type Response, ResponseFailure } from '../../../models';
import {
  isEnumMember,
  type StateSetter,
  useInvertedLocalize,
} from '../../../utilities';
import * as api from '../api';
import { type FactorType, type ProtectedUrlMethods } from '../models';
import {
  privateActions,
  publicActions,
  thunks,
  useDispatch,
  useSelector,
} from '../store';

import EmailOtpForm from './EmailOtpForm';
import IdaasBridge from './IdaasBridge';
import Menu from './Menu';
import { invertedMessages } from './messages';
import MfaError from './MfaError';
import OverlayHelper from './OverlayHelper';
import SmsOtpForm from './SmsOtpForm';
import TotpForm from './TotpForm';
import { type OtpFormProps } from './utilities';
import WindowContext, { type WindowContextValue } from './WindowContext';

import './MfaPromptProvider.css';

const CODE_TOGGLE_DEFAULT = 'true' as const;

const NULL_RESOLVE = () => {};

const NULL_RESOLVE_FACTORY = () => NULL_RESOLVE;

type Verify = (url: string, method: ProtectedUrlMethods) => Promise<boolean>;
const Context = createContext<Verify>(() => Promise.resolve(true));

/**
 * Returns a function that activates the MFA verification prompt and returns a
 * promise conveying the result.
 */
// eslint-disable-next-line react-hooks/rules-of-hooks -- bug in the eslint rule
export const useVerify = (): Verify => useContext(Context);

interface SelectedState {
  isVerificationRequested: boolean;
  onSuccessAction?: Action;
  isMfaFeatureFlagEnabled: boolean;
  isCodeToggleEnabled: boolean;
  requestedUrl?: string;
  requestedMethod?: ProtectedUrlMethods;
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>;
}

enum AlreadyVerifiedState {
  Unknown = 'Unknown',
  Pending = 'Pending',
  AlreadyVerified = 'AlreadyVerified',
  NotAlreadyVerified = 'NotAlreadyVerified',
}

interface MfaPromptProviderProps {
  children?: React.ReactNode;

  /** Wrapper for testing */
  windowLocationAssign?: (url: string) => void;

  /** Wrapper for testing */
  windowOpen?: (url: string, name: string, specs: string) => Window | null;
}

/**
 * Provider that allows children to activate the MFA verification prompt, using
 * one of two methods.
 * 1. Dispatch a "mfaPrompt/verificationRequired" action, and include a
 *    "onSuccessAction" to be dispatched when the user successfully verifies
 *    their identity
 * 2. Invoke the `use` hook and call the returned asynchronous function. Both
 *    approaches require the URL and HTTP Method of a high-risk action to be
 *    provided.
 */
export function MfaPromptProvider({
  children,
  windowLocationAssign = (url) => window.location.assign(url),
  windowOpen = (url, name, specs) => window.open(url, name, specs),
}: MfaPromptProviderProps): JSX.Element {
  const dispatch = useDispatch();
  const [selectedFactorType, setSelectedFactorType] =
    useState<FactorType | null>(null);
  const [alreadyVerifiedStatus, setAlreadyVerifiedStatus] =
    useState<AlreadyVerifiedState>(AlreadyVerifiedState.Unknown);
  const [canGoForward, setCanGoForward] = useState(false);
  const [canGoBack, setCanGoBack] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [resolvePromise, setResolvePromise] =
    useState<(value: boolean | PromiseLike<boolean>) => void>(
      NULL_RESOLVE_FACTORY
    );
  const {
    isVerificationRequested,
    onSuccessAction,
    protectedUrls,
    requestedMethod,
    requestedUrl,
    isMfaFeatureFlagEnabled,
    isCodeToggleEnabled,
  } = useReduxState();
  const [isCodeReadyForSubmission, setIsCodeReadyForSubmission] =
    useState(false);
  const contentBody = useContentBody(
    selectedFactorType,
    setSelectedFactorType,
    setAlreadyVerifiedStatus,
    isCodeReadyForSubmission,
    setIsCodeReadyForSubmission,
    setCanGoForward,
    setResolvePromise,
    resolvePromise,
    protectedUrls,
    setCanGoBack,
    isClosing,
    setIsClosing,
    isVerificationRequested,
    onSuccessAction
  );
  const handleNextClick = () => setIsCodeReadyForSubmission(true);

  const handleBackClick = () => {
    setSelectedFactorType(null);
    setCanGoForward(false);
  };

  const handleClose = () => {
    setSelectedFactorType(null);
    setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);
    dispatch(privateActions.verificationDismissed());
    setCanGoForward(false);
    setResolvePromise(NULL_RESOLVE_FACTORY);
    resolvePromise(false);
  };

  const { className, title, subTitle, footerActionBarComp } =
    useModalIslandProps(
      selectedFactorType,
      handleNextClick,
      handleBackClick,
      canGoForward,
      canGoBack
    );

  const verify = useCallback(
    (url: string, method: ProtectedUrlMethods) => {
      dispatch(publicActions.verificationRequired({ url, method }));

      return new Promise<boolean>((resolve) => {
        setResolvePromise(() => resolve);
      });
    },
    [dispatch]
  );

  const isClosed = useSkipVerificationEffect(
    isVerificationRequested,
    onSuccessAction,
    protectedUrls,
    requestedUrl,
    requestedMethod,
    isMfaFeatureFlagEnabled,
    isCodeToggleEnabled,
    alreadyVerifiedStatus,
    resolvePromise,
    setResolvePromise,
    setAlreadyVerifiedStatus
  );

  const isLoading =
    isClosed &&
    isVerificationRequested &&
    (protectedUrls === undefined ||
      [AlreadyVerifiedState.Unknown, AlreadyVerifiedState.Pending].includes(
        alreadyVerifiedStatus
      ));

  const windowValue = useWindowValue(windowOpen, windowLocationAssign);

  useEffect(() => {
    if (isClosing && !isVerificationRequested) {
      setIsClosing(false);
    }
  }, [isClosing, isVerificationRequested]);

  return (
    <Context.Provider value={verify}>
      {children}

      {isLoading && (
        <OverlayHelper>
          {(zIndex) => (
            <div
              style={{ zIndex }}
              className='Nav_MfaPromptProvider Nav_MfaPromptProvider--isLoading'>
              <Loader kind='loop' format='main' color='brand' />
            </div>
          )}
        </OverlayHelper>
      )}

      <WindowContext.Provider value={windowValue}>
        <ModalIsland
          onClose={handleClose}
          isOpen={!isClosed}
          className={className}
          title={title}
          subtitle={subTitle}
          contentBody={contentBody}
          FooterActionBarComp={footerActionBarComp}
        />
      </WindowContext.Provider>
    </Context.Provider>
  );
}

function useWindowValue(
  windowOpen: (url: string, name: string, specs: string) => Window | null,
  windowLocationAssign: (url: string) => void
): WindowContextValue {
  return useMemo<WindowContextValue>(
    () => ({
      open: (url, name, specs) => windowOpen(url, name, specs),
      locationAssign: (url) => windowLocationAssign(url),
    }),
    [windowOpen, windowLocationAssign]
  );
}

function useContentBody(
  selectedFactorType: FactorType | null,
  setSelectedFactorType: StateSetter<FactorType | null>,
  setAlreadyVerifiedStatus: StateSetter<AlreadyVerifiedState>,
  isCodeReadyForSubmission: boolean,
  setIsCodeReadyForSubmission: StateSetter<boolean>,
  setCanContinue: StateSetter<boolean>,
  setResolvePromise: StateSetter<
    (value: boolean | PromiseLike<boolean>) => void
  >,
  resolvePromise: (value: boolean | PromiseLike<boolean>) => void,
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>,
  setCanGoBack: StateSetter<boolean>,
  isClosing: boolean,
  setIsClosing: StateSetter<boolean>,
  isVerificationRequested: boolean,
  onSuccessAction?: Action
): ReactElement {
  const dispatch = useDispatch();
  const handleFailure = () => setIsCodeReadyForSubmission(false);
  const handleSubmittableChange = (state: boolean) => setCanContinue(state);

  const handleSuccess = () => {
    setIsClosing(true);
    setSelectedFactorType(null);
    setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);
    setIsCodeReadyForSubmission(false);
    notifyOnSuccess(
      dispatch,
      setResolvePromise,
      resolvePromise,
      onSuccessAction
    );
  };

  const handleAutoSelect = (factor: FactorType) => {
    setCanGoBack(false);
    setSelectedFactorType(factor);
  };

  if (isClosing || !isVerificationRequested) {
    return <>{null}</>;
  }

  if (isEnumMember(protectedUrls, ResponseFailure)) {
    const handleRetry = () => {
      dispatch(thunks.requestProtectedUrls());
    };

    return <MfaError onRetry={handleRetry} />;
  }

  if (!selectedFactorType) {
    return (
      <Menu
        onUserSelect={setSelectedFactorType}
        onAutoSelect={handleAutoSelect}
      />
    );
  }

  let SelectedFactorComponent: React.ComponentType<OtpFormProps>;

  switch (selectedFactorType) {
    case 'email':
      SelectedFactorComponent = EmailOtpForm;
      break;
    case 'totp':
      SelectedFactorComponent = TotpForm;
      break;
    case 'sms':
      SelectedFactorComponent = SmsOtpForm;
      break;
    case 'idaas':
      SelectedFactorComponent = IdaasBridge;
      break;
    case 'test':
      throw new Error(
        'For testing menu listing only - not supported for selection'
      );
  }

  return (
    <SelectedFactorComponent
      onSuccess={handleSuccess}
      onFailure={handleFailure}
      isCodeReadyForSubmission={isCodeReadyForSubmission}
      onSubmittableChange={handleSubmittableChange}
    />
  );
}

/**
 * If MFA is not enabled, when the modal is opened, immediately dispatch
 * onSuccessAction. Returns true when this happens.
 */
function useSkipVerificationEffect(
  isVerificationRequested: boolean,
  onSuccessAction: Action | undefined,
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>,
  requestedUrl: string | undefined,
  requestedMethod: ProtectedUrlMethods | undefined,
  isMfaFeatureFlagEnabled: boolean,
  isCodeToggleEnabled: boolean,
  alreadyVerifiedStatus: AlreadyVerifiedState,
  resolvePromise: (value: boolean | PromiseLike<boolean>) => void,
  setResolvePromise: StateSetter<
    (value: boolean | PromiseLike<boolean>) => void
  >,
  setAlreadyVerifiedStatus: StateSetter<AlreadyVerifiedState>
) {
  const dispatch = useDispatch();

  // Assume URL is protected while we wait for the response, so that we don't
  // skip MFA prematurely.
  let isRequestProtected: undefined | boolean;

  if (areProtectedUrlsLoaded(protectedUrls) && requestedUrl) {
    let methodMatchUrls: string[] = [];

    (
      Object.entries(protectedUrls) as [ProtectedUrlMethods, string[]][]
    ).forEach(([key, value]) => {
      if (['ALL', requestedMethod].includes(key)) {
        methodMatchUrls = methodMatchUrls.concat(value);
      }
    });

    isRequestProtected = methodMatchUrls.includes(requestedUrl);
  }

  useEffect(() => {
    doAsyncEffect().catch(() => {});

    async function doAsyncEffect() {
      if (
        !isVerificationRequested ||
        alreadyVerifiedStatus === AlreadyVerifiedState.Pending
      ) {
        return;
      }

      if (
        !isMfaFeatureFlagEnabled ||
        !isCodeToggleEnabled ||
        isRequestProtected === false ||
        alreadyVerifiedStatus === AlreadyVerifiedState.AlreadyVerified
      ) {
        notifyOnSuccess(
          dispatch,
          setResolvePromise,
          resolvePromise,
          onSuccessAction
        );
        setAlreadyVerifiedStatus(AlreadyVerifiedState.Unknown);

        return;
      }

      if (alreadyVerifiedStatus === AlreadyVerifiedState.Unknown) {
        setAlreadyVerifiedStatus(AlreadyVerifiedState.Pending);
        const isAlreadyVerified = await api.checkAlreadyVerified();

        if (isAlreadyVerified) {
          setAlreadyVerifiedStatus(AlreadyVerifiedState.AlreadyVerified);

          return;
        }

        setAlreadyVerifiedStatus(AlreadyVerifiedState.NotAlreadyVerified);

        if (!protectedUrls) {
          dispatch(thunks.requestProtectedUrls());
        }
      }
    }
  }, [
    isVerificationRequested,
    onSuccessAction,
    protectedUrls,
    alreadyVerifiedStatus,
    setAlreadyVerifiedStatus,
    resolvePromise,
    setResolvePromise,
    dispatch,
    requestedUrl,
    isMfaFeatureFlagEnabled,
    isCodeToggleEnabled,
    requestedMethod,
    isRequestProtected,
  ]);

  const shouldShowPrompt =
    isVerificationRequested &&
    isMfaFeatureFlagEnabled &&
    isCodeToggleEnabled &&
    (isRequestProtected === true ||
      isEnumMember(protectedUrls, ResponseFailure)) &&
    alreadyVerifiedStatus === AlreadyVerifiedState.NotAlreadyVerified;

  return !shouldShowPrompt;
}

function areProtectedUrlsLoaded(
  protectedUrls: Response<Record<ProtectedUrlMethods, string[]>>
): protectedUrls is Record<ProtectedUrlMethods, string[]> {
  return (
    protectedUrls !== undefined && !isEnumMember(protectedUrls, ResponseFailure)
  );
}

function useReduxState(): SelectedState {
  const isMfaFeatureFlagEnabled = useSelector(
    (state) => state.user.domainInfo?.FEATURE_MFA === true
  );
  const isActive = useSelector((state) => state.mfaPrompt.isActive);
  const onSuccessAction = useSelector(
    (state) => state.mfaPrompt.onSuccessAction
  );
  const requestedUrl = useSelector((state) => state.mfaPrompt.requestedUrl);
  const requestedMethod = useSelector(
    (state) => state.mfaPrompt.requestedMethod
  );
  const protectedUrls = useSelector((state) => state.mfaPrompt.protectedUrls);

  const isCodeToggleEnabled = !['0', 'false', 'disabled'].includes(
    (
      localStorage.getItem('code-toggle:nav/mfa-prompt') ?? CODE_TOGGLE_DEFAULT
    ).toLowerCase()
  );

  return {
    requestedUrl,
    requestedMethod,
    isMfaFeatureFlagEnabled,
    isCodeToggleEnabled,
    isVerificationRequested: isActive,
    protectedUrls,
    onSuccessAction,
  };
}

function notifyOnSuccess(
  dispatch: Dispatch,
  setResolvePromise: StateSetter<
    (value: boolean | PromiseLike<boolean>) => void
  >,
  resolvePromise: (value: boolean | PromiseLike<boolean>) => void,
  onSuccessAction?: Action
) {
  if (onSuccessAction) {
    dispatch(onSuccessAction);
  }

  dispatch(privateActions.verificationSucceeded());
  resolvePromise(true);
  setResolvePromise(NULL_RESOLVE_FACTORY);
}

function useModalIslandProps(
  selectedFactorType: FactorType | null,
  handleNextClick: () => void,
  handleBackClick: () => void,
  canContinue: boolean,
  canGoBack: boolean
) {
  const availableFactors = useSelector(
    (state) => state.mfaPrompt.availableFactors
  );

  const footerActionBarComp = useFooterActionBarComp(
    selectedFactorType,
    handleNextClick,
    handleBackClick,
    canContinue,
    canGoBack
  );
  const l = useInvertedLocalize(invertedMessages);

  if (selectedFactorType) {
    return {
      className: 'Nav_MfaPromptProvider Nav_MfaPromptProvider--isForm',
      title: l('Enter Your Passcode'),
      subTitle: l(
        'Open your authenticator and enter the passcode to verify your identity.'
      ),
      footerActionBarComp,
    };
  }

  const subTitle = isEnumMember(availableFactors, ResponseFailure)
    ? undefined
    : l('Select one of the methods to continue.');

  return {
    className: 'Nav_MfaPromptProvider Nav_MfaPromptProvider--isMenu',
    title: l('Verify Your Identity'),
    subTitle,
    footerActionBarComp,
  };
}

function useFooterActionBarComp(
  selectedFactorType: FactorType | null,
  handleNextClick: () => void,
  handleBackClick: () => void,
  canContinue: boolean,
  canGoBack: boolean
) {
  const l = useInvertedLocalize(invertedMessages);

  if (!selectedFactorType) {
    return undefined;
  }

  return (
    <FooterActionBar>
      <Box className='Nav_MfaPromptProvider--footer'>
        <div className='FooterActionBar-extraContent'>
          {canGoBack && (
            <Button kind='subtle' size='medium' onClick={handleBackClick}>
              {l('Back')}
            </Button>
          )}
        </div>
        <div className='FooterActionBar-actionButtons'>
          <Button
            kind='filled'
            size='medium'
            onClick={handleNextClick}
            isDisabled={!canContinue}>
            {l('Next')}
          </Button>
        </div>
      </Box>
    </FooterActionBar>
  );
}
